<script lang="ts" setup>
import { useVModel } from '@vueuse/core';
import { XFormField } from '@clicksign/excalibur';
import { MaskaDetail } from 'maska';

import { Token } from '../../schemas/token';

interface InputFieldType {
  value: string,
  ref: string,
}

interface AllowedCodeTypes {
  [key: string]: Element | null;
}

export type OtpInputProps = {
  modelValue: string,
  tokenChannel: string;
  label?: string;
  disabled?: boolean;
  error?: string,
  dataTestid?: string;
};

type OtpInputEmits = {
  (e: 'update:modelValue', token: Token): void,
};

const props = withDefaults(defineProps<OtpInputProps>(), {
  label: '',
  disabled: false,
  tokenChannel: '',
  dataTestid: '',
  error: '',
});

const INPUT_LENGTH = 6;

const emit = defineEmits<OtpInputEmits>();

const token = useVModel(props, 'modelValue', emit, {
  shouldEmit: () => false,
  passive: true,
});

function buildFields(): InputFieldType[] {
  return Array.from(
    { length: INPUT_LENGTH },
    (_, index) => ({
      value: '',
      ref: `field-${index}`,
    }),
  );
}

const fields = ref(buildFields());

const inputSettings = computed(() => {
  const numeric = {
    inputmode: 'numeric' as const,
    mask: '#',
  };
  const alphanumeric = {
    inputmode: 'text' as const,
    mask: '*',
  };

  return props.tokenChannel === 'sms'
    ? numeric
    : alphanumeric;
});

function setFieldsValue(value: string) {
  const formattedValue = value.replace(/\W+|_/g, '')
    .trim()
    .substring(0, INPUT_LENGTH)
    .toUpperCase();

  formattedValue.split('')
    .forEach((character, index) => {
      const field = fields.value[index];

      if (field) field.value = character;
    });
}

function clearFields() {
  fields.value = fields.value.map((field) => ({ ...field, value: '' }));
}

function focusInput(field: HTMLElement) {
  field.focus();
}

function checkIfCanPaste(paste: string) {
  const isNumeric = /^\d+$/.test(paste);

  return props.tokenChannel === 'sms' ? isNumeric : true;
}

function onPaste(event: ClipboardEvent) {
  event.preventDefault();
  const paste = event.clipboardData?.getData('text');

  if (paste && checkIfCanPaste(paste)) setFieldsValue(paste);
}

function onInput(event: CustomEvent<MaskaDetail>) {
  const { target, detail } = event;
  const { value, nextElementSibling } = target as HTMLInputElement;

  if (detail.masked === '') return;

  setFieldsValue(token.value);

  if (value && nextElementSibling) focusInput(<HTMLInputElement>(nextElementSibling));
}

function onKeyDown(event: KeyboardEvent) {
  const { code, target } = event;
  const targetEl = target as HTMLInputElement;
  const { value, previousElementSibling, nextElementSibling } = targetEl;
  const focusChangeKeysToIndex: AllowedCodeTypes = {
    Backspace: value ? targetEl : previousElementSibling as Element | null,
    ArrowLeft: previousElementSibling,
    ArrowRight: nextElementSibling,
  };
  const focusToInput = focusChangeKeysToIndex[code] ?? null;

  if (focusToInput) focusInput(focusToInput as HTMLElement);
}

watch(fields, () => {
  token.value = fields.value.map((field) => field.value.toString()).join('');

  emit('update:modelValue', token.value);
}, { deep: true });

watchEffect(() => {
  if (token.value) setFieldsValue(token.value);
  else clearFields();
});
</script>

<template>
  <XFormField
    :class="[$style.otp, $style.fieldset]"
    :label="label"
    :data-testid="dataTestid"
    :message="error"
    :message-type="error !== '' ? 'error' : undefined"
  >
    <input
      v-for="(field, index) in fields"
      :key="index"
      v-model="field.value"
      v-maska
      :data-maska="inputSettings.mask"
      type="text"
      :class="[$style.field, error && $style.error]"
      :disabled="disabled"
      :inputmode="inputSettings.inputmode"
      autocomplete="one-time-code"
      :data-testid="`${dataTestid}Input${index}`"
      autofocus
      @maska="onInput"
      @paste="onPaste"
      @keydown="onKeyDown"
    >
  </XFormField>
</template>

<style lang="scss" module>
.otp {
  display: flex;
  align-items: center;
  flex-direction: column;
  margin-bottom: var(--space-medium);
}

.fieldset {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  column-gap: var(--space-small-xx);
  border: none;
}

.field {
  width: 30px;
  height: 30px;
  font-weight: var(--font-weight-bold);
  text-align: center;
  text-transform: uppercase;
  border: var(--border-width-small) solid var(--color-neutral-600);
  border-radius: var(--border-radius-small);

  &:disabled {
    color: var(--color-neutral-700);
    background-color: var(--color-neutral-400);
    border-color: var(--color-neutral-400);
    cursor: not-allowed;
  }

  &.error {
    border: var(--border-width-small) solid var(--color-feedback-error-400);
    outline-color: var(--color-feedback-error-400);
  }

  @media (min-width: 768px) {
    width: 60px;
    height: 56px;
    font-size: var(--font-size-large);
  }
}
</style>
